
import Vue from "vue";
import moment from "moment";
import { getDatesFromStartEnd } from "@/utils/settlementUtils";
import { db } from "@/utils/dexie";
import MonthControl from "./MonthControl.vue";
import { chartMixin } from "@/mixins/chartMixin";
import eventBus from "@/eventBus";
import {
  filterSettlementResult,
  OrderPathFilter,
} from "@/utils/orderPathFilter";

import { Chart as ChartJS, registerables, ChartConfiguration } from "chart.js";
ChartJS.register(...registerables);

export default Vue.extend({
  name: "DailyAovChart",
  mixins: [chartMixin],
  components: {
    MonthControl,
  },
  props: {
    calendarValue: Object as () => moment.Moment,
    orderPathFilter: {
      type: Object as () => OrderPathFilter,
      default: undefined,
    },
  },
  data: () => ({
    chart: null as ChartJS | null,
    isRendering: false,
    hasPendingRender: false,
  }),
  watch: {
    calendarValue: {
      handler() {
        this.$nextTick(() => {
          this.render();
        });
      },
      immediate: false,
    },
    "$store.state.mall_info": {
      handler() {
        this.$nextTick(() => {
          this.render();
        });
      },
      deep: true,
    },
    orderPathFilter: {
      handler() {
        this.$nextTick(() => this.render());
      },
      deep: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.render();
    });
  },
  methods: {
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    createOrUpdateChart(ctx: any, config: any) {
      this.destroyChart();
      this.chart = new ChartJS(ctx, config as any) as any;
    },
    async render() {
      if (this.isRendering) {
        this.hasPendingRender = true;
        return;
      }
      this.isRendering = true;

      try {
        const canvasEl = this.$refs.chart as HTMLCanvasElement | undefined;
        if (!canvasEl) return;

        const ctx = canvasEl.getContext("2d");
        if (!ctx) return;

        const shop = this.$store.state.mall_info;
        if (!shop || !shop.mall_id || shop.shop_no === undefined) {
          return;
        }

        const start_date = this.calendarValue
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const end_date = this.calendarValue
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD");
        const dates = getDatesFromStartEnd(start_date, end_date);

        let dbItems = await db.results
          .where("[mall_id+shop_no]")
          .equals([shop.mall_id, String(shop.shop_no)])
          .toArray();

        const hasNoItems = dbItems.length === 0;
        const validShopNo = shop.shop_no !== undefined;
        if (hasNoItems && validShopNo && !isNaN(Number(shop.shop_no))) {
          const numItems = await db.results
            .where("[mall_id+shop_no]")
            .equals([shop.mall_id, Number(shop.shop_no)])
            .toArray();
          if (numItems.length > 0) {
            dbItems = numItems;
          }
        }

        const rows = {} as Record<string, any>;
        for (const item of dbItems) {
          if (item && item.date >= start_date && item.date <= end_date) {
            rows[item.date] = filterSettlementResult(
              item,
              this.orderPathFilter
            );
          }
        }

        const dsPurple = "#7928CA";
        const dsHairline =
          getComputedStyle(document.documentElement)
            .getPropertyValue("--ds-hairline")
            .trim() || "#ebebeb";

        const labels = dates;
        const aovData = labels.map((date) => {
          const r = rows[date];
          if (
            !r ||
            !r.order_count ||
            r.order_count <= 0 ||
            !r.payment_composition_amount
          ) {
            return null;
          }
          return Math.round(r.payment_composition_amount / r.order_count);
        });

        const chartLabels = labels.map((l) => moment(l).format("D"));

        const existingChart = ChartJS.getChart(canvasEl);
        if (existingChart) {
          existingChart.destroy();
        }

        const chartConfig: ChartConfiguration = {
          type: "line",
          data: {
            labels: chartLabels,
            datasets: [
              {
                label: "평균 객단가 (AOV)",
                data: aovData,
                borderColor: dsPurple,
                backgroundColor: "rgba(121, 40, 202, 0.08)",
                borderWidth: 2.5,
                tension: 0.3,
                fill: true,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBackgroundColor: dsPurple,
                spanGaps: true,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              mode: "nearest",
              intersect: false,
            },
            onClick: (event, elements) => {
              if (elements.length > 0) {
                const index = elements[0].index;
                const selectedDate = dates[index];
                eventBus.$emit("query", selectedDate, selectedDate);
              }
            },
            scales: {
              x: {
                grid: {
                  color: dsHairline,
                },
              },
              y: {
                grid: {
                  color: dsHairline,
                },
                ticks: {
                  callback: (value) => `${Number(value).toLocaleString()}원`,
                },
              },
            },
            plugins: {
              legend: {
                display: true,
                position: "left",
                align: "start",
                labels: {
                  boxWidth: 12,
                  boxHeight: 12,
                  font: {
                    family: "Geist, Inter, sans-serif",
                    size: 11,
                  },
                },
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const val = context.raw as number | null;
                    if (val === null || val === undefined) {
                      return "객단가: 데이터 없음";
                    }
                    return `평균 객단가: ${val.toLocaleString()}원`;
                  },
                },
              },
            },
          },
        };

        this.createOrUpdateChart(canvasEl, chartConfig);
      } catch (e) {
        console.error("DailyAovChart render error:", e);
      } finally {
        this.isRendering = false;
        if (this.hasPendingRender) {
          this.hasPendingRender = false;
          this.$nextTick(() => {
            this.render();
          });
        }
      }
    },
  },
});
